import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { useForm } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { JobPositionForm } from 'types/jobPositions';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

export const JOB_POSITION_FORM_CLASSIFIERS = ['JOB_POSITION_TYPE', 'LANGUAGE'];

const defaultValues: JobPositionForm = {
    positionTypeCode: null,
    name: '',
    externalId: '',
    entityTexts: [],
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

interface Props {
    initialData?: JobPositionForm;
    classifiers: Record<string, ClassifierData>;
    entityCustomFields?: CustomField[];
}

/**
 * The job position form's state, shared by the create and edit pages and the cards; the form takes the custom
 * fields' values from the fields it is given.
 */
export const useJobPositionForm = ({ initialData, classifiers, entityCustomFields = EMPTY_CUSTOM_FIELDS }: Props) => {
    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.JOB_POSITION,
        fields: entityCustomFields,
    });

    const methods = useForm<JobPositionForm>({
        defaultValues: initialData
            ? { ...defaultValues, ...initialData, ...initialCustomFieldsObject }
            : { ...defaultValues, ...initialCustomFieldsObject },
        values: initialData ? { ...initialData, ...initialCustomFieldsObject } : undefined,
    });

    const isJobPositionExternalIdEnabled = useIsFeatureEnabled(FeatureFlag.JOB_POSITION_EXTERNAL_ID_ENABLED);
    const isJobPositionTypeEnabled = useIsFeatureEnabled(FeatureFlag.JOB_POSITION_TYPE);

    return {
        methods,
        classifiers,
        customFields,
        initialCustomFieldsObject,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoadingCustomFieldData,
        isJobPositionExternalIdEnabled,
        isJobPositionTypeEnabled,
    };
};

export type JobPositionFormState = ReturnType<typeof useJobPositionForm>;
